import { useDataChannel, useLocalParticipant } from '@livekit/components-react';
import { ReceivedDataMessage } from '@livekit/components-core';
import { useCallback, createContext, useContext } from 'react';
import { useMeetingStore } from '@/stores/meeting.ts';
import { useBoardStore } from '@/stores/board.ts';
import { useUserStore } from '@/stores/user.ts';
import { EVENTS } from '@/consts/events.ts';
import { ParticipantRole } from '@/consts/meeting.ts';
import { DocProps } from '@/types/api';
import { useBoardPdfStore } from '@/stores/boardPdf';
import type { DataPublishOptions } from 'livekit-client';
import type { TLPageId } from 'tldraw';

interface StudentFileUpdateMessage {
  action: 'student-file-add' | 'student-file-remove';
  file: DocProps;
}

interface FileUpdateMessage {
  action: 'file-add' | 'file-remove';
  file: DocProps;
}

type HandUpdateMessage = {
  action: 'hand-up' | 'hand-down';
  studentId: string;
};

type Room = {
  id: number;
  title: string;
  isEditing?: boolean;
};

type CreateOrEditRoomMessage = {
  action: 'create-room' | 'edit-room';
  newRoom: Room;
};

type TransferToCheckRoomMessage = {
  action: string;
  studentId: string;
  roomId: number;
};

type TransferToExitFromRoom = {
  action: string;
  studentsId: string[];
};

type BoardPageChangeMessage = {
  action: 'board-page-change';
  pageId: string;
};

export type SenderFactory = (msg: Uint8Array, options: DataPublishOptions) => void;

type SendContextType = {
  sendStudentStatusUpdate: SenderFactory;
  sendCreateOrEditRoom: SenderFactory;
  sendTransferToCheckRoom: SenderFactory;
  sendTransferToExitFromRoom: SenderFactory;
  sendBoardPageChange: SenderFactory;
  sendFileToStudents: SenderFactory;
  sendUploadFiles: SenderFactory;
};

const SendContext = createContext<SendContextType | undefined>(undefined);

export const SendProvider = ({ children }: { children: React.ReactNode }) => {
  const { setHandUpStatus, setHandUpStudents, setRooms } = useMeetingStore.getState();
  const {
    activeToolPanel,
    studentNotifications,
    incrementStudentNotifications,
    decreaseStudentNotifications
  } = useMeetingStore();

  const { localParticipant } = useLocalParticipant();

  const onStudentStatusUpdate = useCallback(
    (msg: ReceivedDataMessage) => {
      try {
        const { action, studentId } = JSON.parse(
          new TextDecoder().decode(msg.payload)
        ) as HandUpdateMessage;

        if (action === 'hand-up' && studentId) {
          setHandUpStudents((prevStudents) => [...prevStudents, studentId]);
          if (activeToolPanel !== 'participants') {
            incrementStudentNotifications();
          }
        } else if (action === 'hand-down' && studentId) {
          setHandUpStudents((prevStudents) =>
            prevStudents.filter((student) => student !== studentId)
          );
          if (studentNotifications > 0 && activeToolPanel !== 'participants') {
            decreaseStudentNotifications();
          }
        }
      } catch (e) {
        console.error('Failed to hand up or hand down of student', e);
      }
    },
    [
      activeToolPanel,
      studentNotifications,
      incrementStudentNotifications,
      decreaseStudentNotifications
    ]
  );
  const { send: sendStudentStatusUpdate } = useDataChannel(EVENTS.HAND, onStudentStatusUpdate);

  const onCreateOrEditRoom = useCallback((msg: ReceivedDataMessage) => {
    try {
      const { action, newRoom } = JSON.parse(
        new TextDecoder().decode(msg.payload)
      ) as CreateOrEditRoomMessage;

      if (action === 'create-room') {
        setRooms((prevRooms) => [...prevRooms, newRoom]);
      } else if (action === 'edit-room') {
        setRooms((prevRooms) => prevRooms.map((room) => (room.id === newRoom.id ? newRoom : room)));
      }
    } catch (e) {
      console.error('Failed to create or edit room', e);
    }
  }, []);
  const { send: sendCreateOrEditRoom } = useDataChannel(
    EVENTS.CREATE_OR_EDIT_ROOM,
    onCreateOrEditRoom
  );

  const onSendTransferToCheckRoom = useCallback(async (msg: ReceivedDataMessage) => {
    try {
      const { action, studentId, roomId } = JSON.parse(
        new TextDecoder().decode(msg.payload)
      ) as TransferToCheckRoomMessage;
      if (action === 'transfer-student-to-check-room') {
        if (localParticipant.identity === studentId && roomId) {
          setHandUpStatus(false);
          await localParticipant.setAttributes({ roomId: roomId.toString() });
        }
        setHandUpStudents((prevStudents) =>
          prevStudents.filter((student) => student !== studentId)
        );
      }
    } catch (e) {
      console.error('Failed to transfering tutor and student for checking tasks', e);
    }
  }, []);
  const { send: sendTransferToCheckRoom } = useDataChannel(
    EVENTS.TRANSFER_STUDENT_TO_CHECK_TASK,
    onSendTransferToCheckRoom
  );

  const onSendTransferToExitFromRoom = useCallback(async (msg: ReceivedDataMessage) => {
    try {
      const { action, studentsId } = JSON.parse(
        new TextDecoder().decode(msg.payload)
      ) as TransferToExitFromRoom;
      if (action === 'transfer-student-to-exit-from-room') {
        if (studentsId.includes(localParticipant.identity)) {
          await localParticipant.setAttributes({ roomId: '' });
        }
      }
    } catch (e) {
      console.error('Failed to transfering students to exit from room', e);
    }
  }, []);
  const { send: sendTransferToExitFromRoom } = useDataChannel(
    EVENTS.TRANSFER_STUDENT_TO_EXIT_FROM_ROOM,
    onSendTransferToExitFromRoom
  );

  const onBoardPageChange = useCallback((msg: ReceivedDataMessage) => {
    try {
      const { role } = useUserStore.getState();
      if (role !== ParticipantRole.STUDENT) {
        return;
      }

      const { action, pageId } = JSON.parse(
        new TextDecoder().decode(msg.payload)
      ) as BoardPageChangeMessage;

      if (action === EVENTS.BOARD_PAGE_CHANGE && pageId) {
        const currentEditor = useBoardStore.getState().editor;
        if (currentEditor) {
          const pageExists = currentEditor.getPage(pageId as TLPageId);

          if (pageExists) {
            // the page already exists, we can switch immediately.
            currentEditor.setCurrentPage(pageId as TLPageId);
          } else {
            // the page might not exist yet, so we listen to the store
            // until it's created by the sync service
            const unsubscribe = currentEditor.store.listen(
              (entry) => {
                if (entry.changes.added[pageId as TLPageId]) {
                  currentEditor.setCurrentPage(pageId as TLPageId);
                  // unsubscribe immediately after we're done.
                  unsubscribe();
                }
              },
              { source: 'remote', scope: 'document' }
            );
          }
        }
      }
    } catch (e) {
      console.error('Failed to handle board page change', e);
    }
  }, []);

  const { send: sendBoardPageChange } = useDataChannel(EVENTS.BOARD_PAGE_CHANGE, onBoardPageChange);

  const onStudentFileUpdate = useCallback((msg: ReceivedDataMessage) => {
    try {
      const { action, file } = JSON.parse(
        new TextDecoder().decode(msg.payload)
      ) as StudentFileUpdateMessage;

      if (action === 'student-file-add') {
        useBoardPdfStore.getState().addStudentDoc(file);
      } else if (action === 'student-file-remove') {
        useBoardPdfStore.getState().removeStudentDoc(file.id);
        if (useBoardPdfStore.getState().activeDoc?.id === file.id) {
          useBoardPdfStore.getState().setActiveDoc({ id: '', path: '' });
        }
      }
    } catch (e) {
      console.error('Failed to parse student file update message', e);
    }
  }, []);

  const { send: sendFileToStudents } = useDataChannel(
    EVENTS.STUDENT_FILES_UPDATE,
    onStudentFileUpdate
  );

  const onUploadFilesUpdate = useCallback((msg: ReceivedDataMessage) => {
    try {
      const { role } = useUserStore.getState();
      const { action, file } = JSON.parse(
        new TextDecoder().decode(msg.payload)
      ) as FileUpdateMessage;

      if (role == ParticipantRole.ASSISTANT && action === 'file-add') {
        useBoardPdfStore.getState().addUploadDoc(file);
      } else if (action === 'file-remove') {
        useBoardPdfStore.getState().deleteUploadDoc(file.id);
        useBoardPdfStore.getState().removeStudentDoc(file.id);
        if (useBoardPdfStore.getState().activeDoc?.id === file.id) {
          useBoardPdfStore.getState().setActiveDoc({ id: '', path: '' });
        }
      }
    } catch (e) {
      console.error('Failed to parse student file update message', e);
    }
  }, []);

  const { send: sendUploadFiles } = useDataChannel(
    EVENTS.STUDENT_FILES_UPDATE,
    onUploadFilesUpdate
  );

  return (
    <SendContext.Provider
      value={{
        sendStudentStatusUpdate: (msg, options) => sendStudentStatusUpdate(msg, options),
        sendCreateOrEditRoom: (msg, options) => sendCreateOrEditRoom(msg, options),
        sendTransferToCheckRoom: (msg, options) => sendTransferToCheckRoom(msg, options),
        sendTransferToExitFromRoom: (msg, options) => sendTransferToExitFromRoom(msg, options),
        sendBoardPageChange: (msg, options) => sendBoardPageChange(msg, options),
        sendFileToStudents: (msg, options) => sendFileToStudents(msg, options),
        sendUploadFiles: (msg, options) => sendUploadFiles(msg, options)
      }}
    >
      {children}
    </SendContext.Provider>
  );
};

export const useSend = () => {
  const context = useContext(SendContext);
  if (!context) {
    throw new Error('useSend must be used inside <SendProvider>');
  }
  return context;
};
